/**
 * Column-detail and curation clients.
 *
 * The GET and PATCH share one path; PATCH accepts only `description`, `notes`
 * and `synonyms` and rejects an empty body with a 400, so the sparse body is
 * built and validated here rather than at the component.
 */
import { API_ENDPOINTS, apiService } from "../api";
import { isMockApiFlag } from "../apiMode";
import {
  fetchMockColumnDetail,
  patchMockColumnCuration,
} from "./dataStudioAssets.mock";
import {
  deriveQualityStatus,
  mapColumnDetail,
} from "./dataStudioColumnProfileMappers";
import { hasColumnCurationChanges } from "./dataStudioAssets.types";
import type {
  ColumnCurationUpdate,
  ColumnDetail,
} from "./dataStudioAssets.types";
import type {
  GetDataStudioColumnDetailsRequest,
  GetDataStudioColumnDetailsResponse,
  UpdateDataStudioColumnDetailsRequest,
  UpdateDataStudioColumnDetailsResponse,
} from "./dataStudioColumnDetails.types";

const isMockDataStudioApiEnabled = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_DATA_STUDIO_API);

export const buildDataStudioColumnDetailsUrl = (
  connectionId: string,
  assetId: string,
  columnId: string,
): string =>
  API_ENDPOINTS.dataStudio.assetColumn(connectionId, assetId, columnId);

export const getDataStudioColumnDetails = async ({
  connectionId,
  assetId,
  columnId,
}: GetDataStudioColumnDetailsRequest): Promise<GetDataStudioColumnDetailsResponse> => {
  if (isMockDataStudioApiEnabled()) {
    if (process.env.NODE_ENV !== "production") {
      console.log(
        `[MOCK] getDataStudioColumnDetails — request columnId=${columnId}`,
      );
    }
    const detail = await fetchMockColumnDetail(connectionId, assetId, columnId);
    const mapped = mapColumnDetail(detail);
    if (process.env.NODE_ENV !== "production") {
      const score = detail.data_quality_score;
      const band =
        typeof score === "number" ? deriveQualityStatus(score) : "unscored";
      console.log(
        `[MOCK] getDataStudioColumnDetails — response score=${score ?? "null"}, band=${band}`,
      );
    }
    return mapped;
  }

  const detail = await apiService.get<ColumnDetail>(
    buildDataStudioColumnDetailsUrl(connectionId, assetId, columnId),
  );

  return mapColumnDetail(detail);
};

/**
 * Strips fields the caller did not set.
 *
 * The API's semantics are omitted-means-unchanged, so passing `undefined`
 * through to the body would serialize it away anyway — but building the object
 * explicitly is what lets `hasColumnCurationChanges` catch an empty update
 * before it becomes a 400 round trip.
 */
export const toColumnCurationUpdate = (
  request: UpdateDataStudioColumnDetailsRequest,
): ColumnCurationUpdate => {
  const update: ColumnCurationUpdate = {};
  if (request.description !== undefined)
    update.description = request.description;
  if (request.notes !== undefined) update.notes = request.notes;
  if (request.synonyms !== undefined) update.synonyms = request.synonyms;
  return update;
};

export class EmptyColumnCurationUpdateError extends Error {
  constructor() {
    super("No changes to save.");
    this.name = "EmptyColumnCurationUpdateError";
  }
}

export const updateDataStudioColumnDetails = async (
  request: UpdateDataStudioColumnDetailsRequest,
): Promise<UpdateDataStudioColumnDetailsResponse> => {
  const { connectionId, assetId, columnId } = request;
  const update = toColumnCurationUpdate(request);

  /**
   * The API answers an empty body with a 400. Failing here keeps that
   * pointless round trip off the wire while still surfacing a message.
   */
  if (!hasColumnCurationChanges(update)) {
    throw new EmptyColumnCurationUpdateError();
  }

  const detail = isMockDataStudioApiEnabled()
    ? await patchMockColumnCuration(connectionId, assetId, columnId, update)
    : await apiService.patch<ColumnDetail>(
        buildDataStudioColumnDetailsUrl(connectionId, assetId, columnId),
        update,
      );

  return mapColumnDetail(detail);
};
